import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';

interface Props {
  open: boolean;
  designNo: string | null;
  onClose: () => void;
}

const DesignCuttingRecordDialog: React.FC<Props> = ({ open, designNo, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [cuttings, setCuttings] = useState<any[]>([]);
  const [design, setDesign] = useState<any>(null);

  useEffect(() => {
    if (!open || !designNo) return;
    
    const load = async () => {
      try {
        setLoading(true);
        const [dcRes, designRes] = await Promise.all([
          api.get('/design-cutting'),
          api.get(`/designs/${designNo}`)
        ]);

        // Debug: Log the response structure
        console.log('DesignCuttingRecordDialog: API response:', dcRes);
        console.log('DesignCuttingRecordDialog: Design number:', designNo);
        
        // Handle both direct array response and wrapped response
        const allCuttings = Array.isArray(dcRes.data) ? dcRes.data : (Array.isArray(dcRes) ? dcRes : []);
        console.log('DesignCuttingRecordDialog: Total cuttings fetched:', allCuttings.length);
        
        // Filter by design number (case-insensitive comparison)
        const filteredCuttings = allCuttings.filter((d: any) => {
          const match = String(d.design_no || '').toUpperCase() === String(designNo || '').toUpperCase();
          if (match) {
            console.log('DesignCuttingRecordDialog: Found matching cutting:', {
              design_no: d.design_no,
              garment_part: d.garment_part,
              batch_number: d.batch_number,
              status: d.status
            });
          }
          return match;
        });
        
        console.log('DesignCuttingRecordDialog: Filtered cuttings:', filteredCuttings.length);
        
        // For viewing, show all records but group by garment part and batch
        // Sort by batch number (latest first) and status (COMPLETED first)
        const sortedCuttings = filteredCuttings.sort((a: any, b: any) => {
          const aPart = (a.garment_part || '').toUpperCase();
          const bPart = (b.garment_part || '').toUpperCase();
          
          // First sort by garment part (TOP before BOTTOM)
          if (aPart !== bPart) {
            if (aPart === 'TOP') return -1;
            if (bPart === 'TOP') return 1;
            return aPart.localeCompare(bPart);
          }
          
          // Then by status (COMPLETED first)
          if (a.status === 'COMPLETED' && b.status !== 'COMPLETED') return -1;
          if (b.status === 'COMPLETED' && a.status !== 'COMPLETED') return 1;
          
          // Then by batch number (highest first)
          const aBatch = a.batch_number || 1;
          const bBatch = b.batch_number || 1;
          return bBatch - aBatch;
        });
        
        console.log('DesignCuttingRecordDialog: Sorted cuttings:', sortedCuttings.length);
        
        // For display, we'll show the latest batch per garment part (to avoid confusion)
        // But if user wants to see all batches, we can modify this later
        const latestByPart: { [part: string]: any } = {};
        sortedCuttings.forEach((c: any) => {
          const gp = (c.garment_part || '').toUpperCase();
          if (!latestByPart[gp]) {
            latestByPart[gp] = c;
          }
        });
        
        const finalCuttings = Object.values(latestByPart);
        console.log('DesignCuttingRecordDialog: Final cuttings (latest per part):', finalCuttings.length);
        console.log('DesignCuttingRecordDialog: Final cuttings details:', finalCuttings.map((c: any) => ({
          garment_part: c.garment_part,
          batch_number: c.batch_number,
          status: c.status
        })));
        setCuttings(finalCuttings);
        
        // Handle design response
        const designData = designRes?.data || designRes || null;
        setDesign(designData);
      } catch (e: any) {
        console.error('Error loading design cutting records:', e);
        console.error('Error details:', {
          message: e.message,
          response: e.response?.data,
          status: e.response?.status
        });
        setCuttings([]);
        setDesign(null);
      } finally {
        setLoading(false);
      }
    };
    
    load();
  }, [open, designNo]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getTotalPieces = (cutting: any) => {
    return (cutting.xs_pieces || 0) + 
           (cutting.s_pieces || 0) + 
           (cutting.m_pieces || 0) + 
           (cutting.l_pieces || 0) + 
           (cutting.xl_pieces || 0) + 
           (cutting.xxl_pieces || 0) + 
           (cutting.three_xl_pieces || 0);
  };

  const sizeFields = [
    { key: 'xs_pieces', label: 'XS' },
    { key: 's_pieces', label: 'S' },
    { key: 'm_pieces', label: 'M' },
    { key: 'l_pieces', label: 'L' },
    { key: 'xl_pieces', label: 'XL' },
    { key: 'xxl_pieces', label: 'XXL' },
    { key: 'three_xl_pieces', label: '3XL' }
  ];

  const topCutting = cuttings.find((c: any) => (c.garment_part || '').toUpperCase() === 'TOP');
  const bottomCutting = cuttings.find((c: any) => (c.garment_part || '').toUpperCase() === 'BOTTOM');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Design Cutting Record - {designNo}
        <IconButton onClick={onClose} aria-label="close" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Design Information */}
            {design && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Design Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Design Number</Typography>
                      <Typography variant="body1" fontWeight="bold">{design.design_no || designNo}</Typography>
                    </Grid>
                    {design.name && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Design Name</Typography>
                        <Typography variant="body1">{design.name}</Typography>
                      </Grid>
                    )}
                    {design.product_type && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Product Type</Typography>
                        <Typography variant="body1">{design.product_type}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* TOP Section */}
            {topCutting && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip label="TOP" color="primary" />
                    <Typography variant="h6">TOP Design Cutting Record</Typography>
                    {topCutting.batch_number && (
                      <Chip label={`Batch #${topCutting.batch_number}`} size="small" />
                    )}
                    <Chip 
                      label={topCutting.status || 'PENDING'} 
                      size="small" 
                      color={topCutting.status === 'COMPLETED' ? 'success' : 'warning'} 
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Received Date</Typography>
                      <Typography variant="body1">{formatDate(topCutting.received_date)}</Typography>
                    </Grid>
                    {topCutting.inner_received_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Inner Received Date</Typography>
                        <Typography variant="body1">{formatDate(topCutting.inner_received_date)}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Fabric Meter</Typography>
                      <Typography variant="body1">{topCutting.fabric_meter || 0} m</Typography>
                    </Grid>
                    {topCutting.inner_fabric_meter && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Inner Fabric Meter</Typography>
                        <Typography variant="body1">{topCutting.inner_fabric_meter} m</Typography>
                      </Grid>
                    )}
                    {topCutting.cutting_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Cutting Date</Typography>
                        <Typography variant="body1">{formatDate(topCutting.cutting_date)}</Typography>
                      </Grid>
                    )}
                    {topCutting.challan_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Challan Date</Typography>
                        <Typography variant="body1">{formatDate(topCutting.challan_date)}</Typography>
                      </Grid>
                    )}
                    {topCutting.rate && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Rate</Typography>
                        <Typography variant="body1">₹{topCutting.rate}</Typography>
                      </Grid>
                    )}
                    {topCutting.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography variant="body1">{topCutting.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>Size Details</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {sizeFields.map(({ label }) => (
                          <TableCell key={label} align="center"><strong>{label}</strong></TableCell>
                        ))}
                        <TableCell align="center"><strong>Total</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {sizeFields.map(({ key, label }) => (
                          <TableCell key={label} align="center">{topCutting[key] || 0}</TableCell>
                        ))}
                        <TableCell align="center"><strong>{getTotalPieces(topCutting)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}

            {/* BOTTOM Section */}
            {bottomCutting && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip label="BOTTOM" color="secondary" />
                    <Typography variant="h6">BOTTOM Design Cutting Record</Typography>
                    {bottomCutting.batch_number && (
                      <Chip label={`Batch #${bottomCutting.batch_number}`} size="small" />
                    )}
                    <Chip 
                      label={bottomCutting.status || 'PENDING'} 
                      size="small" 
                      color={bottomCutting.status === 'COMPLETED' ? 'success' : 'warning'} 
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Received Date</Typography>
                      <Typography variant="body1">{formatDate(bottomCutting.received_date)}</Typography>
                    </Grid>
                    {bottomCutting.inner_received_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Inner Received Date</Typography>
                        <Typography variant="body1">{formatDate(bottomCutting.inner_received_date)}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Fabric Meter</Typography>
                      <Typography variant="body1">{bottomCutting.fabric_meter || 0} m</Typography>
                    </Grid>
                    {bottomCutting.inner_fabric_meter && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Inner Fabric Meter</Typography>
                        <Typography variant="body1">{bottomCutting.inner_fabric_meter} m</Typography>
                      </Grid>
                    )}
                    {bottomCutting.cutting_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Cutting Date</Typography>
                        <Typography variant="body1">{formatDate(bottomCutting.cutting_date)}</Typography>
                      </Grid>
                    )}
                    {bottomCutting.challan_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Challan Date</Typography>
                        <Typography variant="body1">{formatDate(bottomCutting.challan_date)}</Typography>
                      </Grid>
                    )}
                    {bottomCutting.rate && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Rate</Typography>
                        <Typography variant="body1">₹{bottomCutting.rate}</Typography>
                      </Grid>
                    )}
                    {bottomCutting.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography variant="body1">{bottomCutting.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>Size Details</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {sizeFields.map(({ label }) => (
                          <TableCell key={label} align="center"><strong>{label}</strong></TableCell>
                        ))}
                        <TableCell align="center"><strong>Total</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {sizeFields.map(({ key, label }) => (
                          <TableCell key={label} align="center">{bottomCutting[key] || 0}</TableCell>
                        ))}
                        <TableCell align="center"><strong>{getTotalPieces(bottomCutting)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}

            {cuttings.length === 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No design cutting records found for {designNo}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DesignCuttingRecordDialog;

